// Phase 0b PR 9: TanStack Query bindings for the contract activation
// gate. The gate is seeded by CommitImport (PR 8) and lives at
// /delivery/programs/:id?tab=activation-gate.
import { Code, ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../../lib/connect/clients';
const GATE_KEY = 'activation-gate';
export function useActivationGate(contractProjectId) {
    return useQuery({
        queryKey: [
            GATE_KEY,
            contractProjectId
        ],
        enabled: !!contractProjectId,
        queryFn: async ()=>{
            // NotFound is a normal state, not an error: only CommitImport seeds a
            // gate, so direct-upload / manually-created programs never have one.
            // zou answers CodeNotFound for those (activation_gate.go loadActivationGate)
            // — map it to null so consumers (nav, page) can branch without retries.
            try {
                const resp = await projectDataClient.getActivationGate({
                    contractProjectId
                });
                return resp.gate ?? null;
            } catch (err) {
                if (err instanceof ConnectError && err.code === Code.NotFound) return null;
                throw err;
            }
        }
    });
}
export function useUpdateActivationGateItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.updateActivationGateItem({
                contractProjectId: args.contractProjectId,
                itemKey: args.itemKey,
                state: args.state,
                reason: args.reason ?? ''
            });
            return resp.gate;
        },
        onSuccess: (_gate, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    GATE_KEY,
                    vars.contractProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to update activation gate item');
        }
    });
}
/**
 * ActivateContract mutation. The handler returns `gate` on success and
 * `violations[]` (still-Pending item_keys) on failure; callers branch on
 * the violations array so the failure path doesn't have to throw.
 */ export function useActivateContract() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.activateContract({
                contractProjectId: args.contractProjectId
            });
            return resp;
        },
        onSuccess: (resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    GATE_KEY,
                    vars.contractProjectId
                ]
            });
            if (!resp.violations || resp.violations.length === 0) {
                toast.success('Contract activated');
            }
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to activate contract');
        }
    });
}
